import axiosAPI from './axios'
import { APPSYNC_ENDPOINT } from './endpoints'
import { Poll, POLL_STATUS } from '../modals/CreatedPollModal/PollDataModel'

// POLLS

export const getAllPollsForEvent = async (
  eventId: string = '',
  limit?: Number,
  nextToken?: string,
  status?: POLL_STATUS
) => {
  try {
    // Build dynamic filtering based on what was passed
    let filterConditions = []
    if (eventId.trim().length > 0) {
      filterConditions.push(`eventId: {eq: "${eventId}"}`)
    }
    if (status) {
      filterConditions.push(`status: {eq: "${status}"}`)
    }

    const filterstring =
      filterConditions.length > 0
        ? `filter: {${filterConditions.join(', ')}}`
        : ''

    let paginationParams = []
    if (limit) {
      paginationParams.push(`limit: ${limit}`)
    }
    if (nextToken) {
      paginationParams.push(`nextToken: "${nextToken}"`)
    }

    const allParams = [filterstring, ...paginationParams]
      .filter(Boolean)
      .join(', ')
    const queryParams = allParams ? `(${allParams})` : ''

    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
                  query ListPolls {
                      listGatherApiPolls${queryParams} {
                        items {
                          uniqueLiveEventEntityId
                          eventId
                          question
                          responses
                          status
                          creationDateTimeStampSeconds
                          pushedDateTimeStampSeconds
                        }
                        nextToken
                      }
                    }
                  `,
      },
    })

    if (response.data.errors) {
      console.error('AppSync error for polls retrieval: ', response.data.errors)
      throw new Error('Failed to retrieve polls')
    }

    return response.data.data.listGatherApiPolls.items
  } catch (error) {
    console.error('AppSync error for polls retrieval: ', error)
    throw error
  }
}

export const createNewPollForEvent = async (
  eventId: string,
  email: string,
  question: string,
  answers: { id: string; answer: string }[]
) => {
  try {
    const responses = answers
      .filter((item) => item.answer.trim().length !== 0)
      .map((answer: any) => answer.answer)

    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
          mutation CreatePoll($eventId: String!, $email: String!, $question: String!, $responses: [String]!) {
            createGatherApiPoll(input: {
              eventId: $eventId,
              participantEmail: $email,
              question: $question,
              responses: $responses
            }) {
              eventId
              uniqueLiveEventEntityId
              question
              responses
              status
            }
          }
        `,
        variables: { eventId, email, question, responses },
      },
    })

    if (response.data.errors || !response.data.data.createGatherApiPoll) {
      console.error(
        'createGatherApiPoll mutation failed - backend resolver issue'
      )
      throw new Error('Failed to create poll')
    }

    return response.data.data.createGatherApiPoll
  } catch (error) {
    console.error('AppSync error for poll creation: ', error)
    throw error
  }
}

export const deletePollForEvent = async (eventId: string, pollId: string) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
                    mutation DeletePoll {
                      deleteGatherApiPoll(input: {eventId: "${eventId}", uniqueLiveEventEntityId: "${pollId}"}) {
                          eventId
                          uniqueLiveEventEntityId
                      }
                    }
                  `,
      },
    })
    if (response.data.errors) {
      console.error('AppSync error for poll deletion: ', response.data.errors)
      throw new Error('Failed to delete poll')
    }

    return response.data.data.deleteGatherApiPoll
  } catch (error) {
    console.error('AppSync error for poll deletion: ', error)
    throw error
  }
}

export const deleteAllPollsForEvent = async (
  eventId: string,
  polls: Poll[]
) => {
  try {
    const deletePromises = polls.map(async (poll: Poll) => {
      const response = await axiosAPI.request({
        url: APPSYNC_ENDPOINT(),
        method: 'POST',
        data: {
          query: `
                        mutation DeletePolls {
                          deleteGatherApiPoll(input: {eventId: "${eventId}", uniqueLiveEventEntityId: "${poll.pollId}"}) {
                              eventId
                              uniqueLiveEventEntityId
                          }
                        }
                      `,
        },
      })

      if (response.data.errors) {
        throw new Error(`Failed to delete poll ${poll.pollId}`)
      }

      return response.data.data.deleteGatherApiPoll
    })

    await Promise.all(deletePromises)
  } catch (error) {
    console.error('AppSync error for all polls deletion: ', error)
    throw error
  }
}

export const getSinglePollForEvent = async (
  eventId: string,
  pollId: string
) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
                    query getPoll {
                      getGatherApiPoll(input: {eventId: "${eventId}", uniqueLiveEventEntityId: "${pollId}"}) {
                        uniqueLiveEventEntityId
                        eventId
                        question
                        responses
                        status
                        creationDateTimeStampSeconds
                        pushedDateTimeStampSeconds
                      }
                    }
                  `,
      },
    })
    if (response.data.errors) {
      console.error('AppSync error for poll get: ', response.data.errors)
      throw new Error('Failed to get poll')
    }

    return response.data.data.getGatherApiPoll
  } catch (error) {
    console.error('AppSync error for poll get: ', error)
    throw error
  }
}

export const editPollForEvent = async (
  eventId: string,
  pollId: string,
  question: string,
  responses: string[]
) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
              mutation EditPoll($eventId: String!, $pollId: String!, $question: String!, $responses: [String]!) {
                updateGatherApiPoll(input: {
              eventId: $eventId,
              uniqueLiveEventEntityId: $pollId,
              question: $question,
              responses: $responses
            }) {
                    eventId
                    uniqueLiveEventEntityId
                    status
                    question
                    responses
                }
              }
            `,
        variables: {
          eventId: eventId,
          pollId: pollId,
          question: question,
          responses: responses,
        },
      },
    })
    if (response.data.errors) {
      console.error('AppSync error for poll edit: ', response.data.errors)
      throw new Error('Failed to edit poll')
    }

    return response.data.data.updateGatherApiPoll
  } catch (error) {
    console.error('AppSync error for poll edit: ', error)
    throw error
  }
}

export const updatePollStatus = async (
  eventId: string,
  pollId: string,
  status: POLL_STATUS
) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
              mutation UpdatePollStatus($eventId: String!, $pollId: String!, $status: String!) {
                updateGatherApiPoll(input: {
                  eventId: $eventId,
                  uniqueLiveEventEntityId: $pollId,
                  status: $status
                }) {
                    eventId
                    uniqueLiveEventEntityId
                    status
                    question
                    responses
                }
              }
            `,
        variables: {
          eventId: eventId,
          pollId: pollId,
          status: status,
        },
      },
    })
    if (response.data.errors) {
      console.error(
        'AppSync error for poll status update: ',
        response.data.errors
      )
      throw new Error('Failed to update poll status')
    }

    return response.data.data.updateGatherApiPoll
  } catch (error) {
    console.error('AppSync error for poll status update: ', error)
    throw error
  }
}

export const getParticipantResponse = async (
  eventId: string,
  pollId: string,
  participantEmail: string
) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
                    query GetUserPollResponse($pollId: String!, $participantEmail: String!, $eventId: String!) {
                      getGatherApiPollResponseForUser(
                        uniqueLiveEventEntityId: $pollId,
                        participantEmail: $participantEmail,
                        eventId: $eventId
                      ) {
                        uniqueLiveEventEntityId
                        entityIdForPoll
                        eventId
                        participantEmail
                        response
                      }
                    }
                  `,
        variables: {
          pollId: pollId,
          participantEmail: participantEmail,
          eventId: eventId,
        },
      },
    })
    if (response.data.errors) {
      console.error(
        'AppSync error for user poll response: ',
        response.data.errors
      )
      throw new Error('Failed to get user poll response')
    }

    return response.data.data.getGatherApiPollResponseForUser
  } catch (error) {
    console.error('AppSync error for user poll response: ', error)
    throw error
  }
}

export const submitParticipantResponse = async (
  eventId: string,
  pollId: string,
  participantEmail: string,
  responseKey: string
) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
                    mutation SubmitPollResponse {
                      createGatherApiPollResponse(input: {eventId: "${eventId}", entityIdForPoll: "${pollId}", participantEmail: "${participantEmail}", response: "${responseKey}"}) {
                          uniqueLiveEventEntityId
                          entityIdForPoll
                          eventId
                          participantEmail
                          response
                      }
                    }
                  `,
      },
    })
    if (response.data.errors) {
      console.error(
        'AppSync error for poll response submission: ',
        response.data.errors
      )
      throw new Error('Failed to submit poll response')
    }

    return response.data.data.createGatherApiPollResponse
  } catch (error) {
    console.error('AppSync error for poll response submission: ', error)
    throw error
  }
}

export const getPollResults = async (eventId: string, pollId?: string) => {
  try {
    const response = await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: 'POST',
      data: {
        query: `
          query GetPollResults($eventId: String!, $pollId: String) {
            getPollResults(input: {eventId: $eventId, pollId: $pollId}) {
              eventId
              participationRate
              pollId
              question
              totalCheckedInAttendees
              totalVotes
              responseOptions {
                option
                percentage
                votes
              }
            }
          }
        `,
        variables: {
          eventId: eventId,
          pollId: pollId,
        },
      },
    })

    if (response.data.errors) {
      console.error('AppSync error for poll results: ', response.data.errors)
      throw new Error('Failed to get poll results')
    }

    return response.data.data.getPollResults
  } catch (error) {
    console.error('AppSync error for poll results: ', error)
    throw error
  }
}

// MATCHMAKER
export const adminMatchmakerTrigger = async (eventId: string, participantEmail: string) => {
  try {
    await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: "POST",
      data: {
        query: `mutation triggerMatchmaker($eventId: String!, $participantEmail: String!) {
                startMatchmaker(
          input: {eventId: $eventId, participantEmail: $participantEmail}
        ) {
          liveEntityType
          creationDateTimeStampSeconds
          participantOneTitle
          participantTwoTitle
          participantThreeEmail
          participantThreeName
          participantThreeTitle
          subscriptionTriggerEmail
        }
        }`,
        variables: {
          eventId: eventId,
          participantEmail: participantEmail,
        }
      }
    })
  } catch (error) {
    console.error('AppSync error for matchmaker trigger: ', error)
    throw error
  }
}

export const adminStartMatchingTrigger = async (eventId: string, participantEmail: string) => {
  try {
    await axiosAPI.request({
      url: APPSYNC_ENDPOINT(),
      method: "POST",
      data: {
        query: `mutation triggerMatching($eventId: String!, $participantEmail: String!) {
                createMatchmakerPairs(
          input: {eventId: $eventId, participantEmail: $participantEmail}
        ) {
          creationDateTimeStampSeconds
          eventId
          liveEntityType
          participantEmails
          participantOneEmail
          participantOneName
          participantOneTitle
          participantThreeEmail
          participantThreeName
          participantThreeTitle
          participantTwoEmail
          participantTwoName
          participantTwoTitle
          subscriptionTriggerEmail
          uniqueLiveEventEntityId
        }
        }`,
        variables: {
          eventId: eventId,
          participantEmail: participantEmail,
        }
      }
    })
  } catch (error) {
    console.error('AppSync error for matchmaker matching trigger: ', error)
    throw error
  }
}

